import { MfaMethod } from "../Services/elector-data.service";

export class EmptyFeatureSetResponse implements FeatureSetResponse {
    declarationSettings?: FeatureSetDeclarationSettings | undefined;
    featureSetFields: FeatureSetFields;
    featureSetName: string;

    constructor() {
        this.featureSetFields = { fields: [
            new FeatureSetField('Voting_Pin', 'string', 'Voting PIN', 'Please enter your Voting PIN', true, true, true, false),
            new FeatureSetField('Birth_Date', 'date', 'Birth Date', 'Please enter your Birth Date', true, false, true, false)
        ] }
        this.featureSetName = 'default'
    }
}

export class EmptyRegistrationFeatureSetResponse implements FeatureSetResponse {
    declarationSettings?: FeatureSetDeclarationSettings | undefined;
    featureSetFields: FeatureSetFields;
    featureSetName: string;
    constructor() {
        this.featureSetFields = { fields: [
            new FeatureSetField('Barcode', 'string', 'Registration PIN', 'Please enter your Registration PIN', true, false, false, false),
            new FeatureSetField('Birth_Date', 'date', 'Date of Birth', 'Please enter your Date of Birth', true, false, false, false)
        ] }
        this.featureSetName = 'default'
    }
}

export interface FeatureSetResponse {
    declarationSettings?: FeatureSetDeclarationSettings,
    featureSetFields: FeatureSetFields,
    featureSetName: string,
    registrationSettings?: FeatureSetRegistrationSettings,
    pickerOptions?: FeatureSetPickerOptions,
    miscSettings?: FeatureSetMiscSettings,
    scanSettings?: FeatureSetScanSettings,
    orgId?: number
}

export interface FeatureSetMiscSettings {
    header?: string,
    subHeader?: string,
    vcSubHeader?: string,
    registrationStartTimestamp?: string,
    registrationEndTimestamp?: string,
    electionEndTimestamp?: string,
    electionStartTimestamp?: string,
    vcStartTimestamp?: string,
    vcEndTimestamp?: string,
    ballotReturnOptions?: BallotReturnOptions,
    mfaMethods?: MfaMethod[]
}

export interface FeatureSetScanSettings {
    barcodeTypeToScan: {types: string[]},
    scanIdString: {strings: string[]},
    scanToLogin: boolean
  }
  
export interface BallotReturnOptions {
    fields?: FeatureSetField[],
    electronicOrPrintValue?: string,
    printValue?: string
}

export interface FeatureSetDeclarationSettings {
    declarationMandatory: boolean,
    declarationText: string,
}

export interface FeatureSetFields {
    fields: FeatureSetField[],
}

export interface FeatureSetField {
    fieldName: string,
    fieldType: string, 
    fieldTitle: string,
    fieldInvalidMessage: string,
    isMandatory: boolean,
    isKey: boolean,
    sendInOidvData: boolean,
    sendInIdProfile: boolean,
    isEventFilter?: boolean,
    fieldTooltip?: string,
    isRegistrationKey?: boolean,
    isPinDeliveryChannel?: boolean,
    isPin?: boolean
}

export class FeatureSetField implements FeatureSetField {
    constructor(
        fieldName: string,
        fieldType: string, 
        fieldTitle: string,
        fieldInvalidMessage: string,
        isMandatory: boolean,
        isKey: boolean,
        sendInOidvData: boolean,
        sendInIdProfile: boolean,
        isEventFilter?: boolean,
        fieldTooltip?: string,
        isRegistrationKey?: boolean,
        isPinDeliveryChannel?: boolean,
        isPin?: boolean
        ) {
            this.fieldName = fieldName
            this.fieldType = fieldType
            this.fieldTitle = fieldTitle
            this.fieldInvalidMessage = fieldInvalidMessage
            this.isMandatory = isMandatory
            this.isKey = isKey
            this.sendInOidvData = sendInOidvData
            this.sendInIdProfile = sendInIdProfile
            this.isEventFilter = isEventFilter
            this.fieldTooltip = fieldTooltip
            this.isRegistrationKey = isRegistrationKey
            this.isPinDeliveryChannel = isPinDeliveryChannel
            this.isPin = isPin
        }
}

export interface FeatureSetRegistrationSettings {
    fields: FeatureSetField[],
    titleText?: string,
    headerText?: string,
    alreadyRegisteredErrorText?: string,
    verificationFailedErrorText?: string,
    emailSubject?: string,
    emailMessage?: string,
    smsMessage?: string,
    enablePinDelivery?: boolean
}

export interface FeatureSetPickerOptions {
    options: FeatureSetPickerOption[] 
}

export interface FeatureSetPickerOption {
    optionName: string,
    optionValues: string[],
}

export class ConfirmFeatureSetField extends FeatureSetField {
    confirmingField: string
    
    constructor(field: FeatureSetField) {
        super('Confirm_' + field.fieldName, 'confirm_' + field.fieldType, $localize`Confirm ` + field.fieldTitle, '', field.isMandatory, false, false, false)
        this.confirmingField = field.fieldName
    }
}

export class DateFeatureSetField extends FeatureSetField {
    dateParts: Set<'day' | 'month' | 'year'>;

    constructor(field: FeatureSetField, dateParts: Set<'day' | 'month' | 'year'>) {
        super(field.fieldName, 'date', field.fieldTitle, field.fieldInvalidMessage, field.isMandatory, field.isKey, field.sendInOidvData, field.sendInIdProfile)
        this.dateParts = dateParts
    }
}

export const IDFlowFeatureSetFields: FeatureSetFields = { fields: [
    new FeatureSetField('Email', 'email', $localize`Email`, $localize`Please enter a valid email address.`, true, true, true, true, false, $localize`Please enter the email address in your voter registration file. You will receive your PIN at this address.`),
    new FeatureSetField('Mobile', 'phone', $localize`Phone Number`, $localize`Please enter a valid phone number.`, false, false, true, true, false, $localize`Please enter the phone number in your voter registration file.`)
]}

const PasswordFeatureSetField = new FeatureSetField('Password', 'password', $localize`Password`, $localize`Passwords must be at least 12 characters long.`, true, true, true, true, false, $localize`Please enter your password.`);
const ConfirmPasswordFeatureSetField = new FeatureSetField('Confirm_Password', 'confirm_password', $localize`Confirm Password`, $localize`Passwords must match.`, true, true, true, true, false, $localize`Please enter the same password as you did above.`);

export const CreatePasswordFeatureSetFields: FeatureSetField[] = [
    new FeatureSetField('Email', 'voter_id', $localize`Email`, $localize`Please enter a valid email address.`, true, true, true, true, false, $localize`Please enter the email address in your voter registration file. You will receive your PIN at this address.`),
    PasswordFeatureSetField,
    ConfirmPasswordFeatureSetField
]

export const EnterPasswordFeatureSetFields: FeatureSetField[] = [
    new FeatureSetField('Email', 'voter_id', $localize`Email`, $localize`Please enter a valid email address.`, true, true, true, true, false, $localize`Please enter the email address in your voter registration file. You will receive your PIN at this address.`),
    PasswordFeatureSetField
]